export type EventListener<T> = (value: T) => unknown;

const EVENT_NOT_EMITTED = Symbol("not emitted");

export class Event<T> {
    private _listeners: EventListener<T>[] = [];

    lastEmitted: T | typeof EVENT_NOT_EMITTED = EVENT_NOT_EMITTED;

    isEmitted() {
        return this.lastEmitted !== EVENT_NOT_EMITTED;
    }

    emit(value: T) {
        this.lastEmitted = value;
        for (const listener of this._listeners) {
            listener(value);
        }
    }

    on(listener: EventListener<T>) {
        this._listeners.push(listener);
    }

    off(listener: EventListener<T>) {
        const idx = this._listeners.indexOf(listener);
        if (idx === -1) {
            return false;
        }

        this._listeners.splice(idx, 1);
    }

    onWith(listener: EventListener<T>) {
        this.on(listener);
        return () => this.off(listener);
    }
}

class SuppressableEvent<T> extends Event<T> {
    private _suppresser: EventListener<T> | undefined;

    emit(value: T): void {
        if (this._suppresser) {
            this._suppresser(value);
            return;
        }

        super.emit(value);
    }

    suppress(cb: EventListener<T>) {
        this._suppresser = cb;
    }

    stopSuppression() {
        this._suppresser = undefined;
    }
}

class SearchEvent extends SuppressableEvent<void> {
    init() {
        const executedTwiceInARowDelay = 300;
        let lastExecuted = 0;
        window.addEventListener("keydown", (event) => {
            const execTime = Date.now();
            const timeouted =
                execTime - lastExecuted < executedTwiceInARowDelay;
            const isCtrlOrCmd = event.ctrlKey || event.metaKey;
            const isFKey = event.code === "KeyF";

            if (isCtrlOrCmd && isFKey && !timeouted) {
                event.preventDefault();
                this.emit();
                lastExecuted = execTime;
            }
        });
    }
}

class EventsImpl {
    ui = {
        search: new SearchEvent(),
        sidebar: new Event<"search" | "message-pin" | "contacts" | "closed">(),
        messageArea: {
            jumpToBottom: new Event<boolean>(),
        },
    };

    init() {
        this.ui.search.init();
    }
}

export const EventBus = new EventsImpl();
